import { Link } from 'react-router-dom';
import Chip from '@mui/material/Chip';
import HandshakeOutlinedIcon from '@mui/icons-material/HandshakeOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import HubOutlinedIcon from '@mui/icons-material/HubOutlined';
import SecurityOutlinedIcon from '@mui/icons-material/SecurityOutlined';
import type { SvgIconComponent } from '@mui/icons-material';
import { PageHero } from '@/components/PageHero';
import { NotifyForm } from '@/components/NotifyForm';
import { Reveal } from '@/components/Reveal';
import { useSeo } from '@/seo/useSeo';

type Belief = { icon: SvgIconComponent; title: string; body: string };

const beliefs: Belief[] = [
  {
    icon: VisibilityOutlinedIcon,
    title: 'Truth before convenience',
    body: 'A module is labeled by what\'s actually true today — live, available on configure, building, or roadmap — never by what would look better on a landing page. If a claim would be more flattering than accurate, we\'d rather be less flattering.',
  },
  {
    icon: HubOutlinedIcon,
    title: 'One platform beats a stitched stack',
    body: 'We didn\'t start by asking "what features sell." We started by running our own MSP practice on six separate tools and getting tired of the seams between them. Nexus is the platform we wished existed.',
  },
  {
    icon: SecurityOutlinedIcon,
    title: 'Security is a default, not an upsell',
    body: 'Tenant isolation, encrypted secrets, and signed remote execution aren\'t a higher tier — they\'re the architecture every tenant runs on, including a design-partner cohort of one.',
  },
  {
    icon: HandshakeOutlinedIcon,
    title: 'Dogfood before we ask you to',
    body: 'We run our own MSP practice on Nexus — real tickets, real devices, real client organizations — before any capability is offered to a design partner, let alone claimed publicly.',
  },
];

export function About(): JSX.Element {
  useSeo({
    title: 'About',
    description:
      'Nexus is built by Elemental TS, an MSP that got tired of running its own practice on six stitched-together tools. We build in the open and dogfood everything before we ask you to run it.',
    path: '/about',
  });

  return (
    <div>
      <PageHero
        eyebrow="About Nexus"
        title="We built the platform we couldn't find."
        subtitle="Nexus is built by Elemental TS, a small MSP that got tired of running its own client operations across six stitched-together tools — a PSA here, an RMM there, backup as a separate line item, security as another. We built the platform we wished we could buy, and now we're opening it to other MSPs who are running into the same wall."
      >
        <Reveal delay={150}>
          <div className="mt-9 flex justify-center">
            <NotifyForm />
          </div>
        </Reveal>
      </PageHero>

      <section className="py-16 md:py-24">
        <div className="container-content max-w-[760px]">
          <Reveal>
            <h2 className="font-display text-2xl font-bold text-nexus-ink md:text-3xl">Where we are today</h2>
            <p className="mt-4 text-[15px] leading-relaxed text-white/75">
              We&apos;re a small team, and we&apos;d rather say that plainly than perform a bigger one.
              Nexus runs our own MSP practice today — helpdesk, endpoint management, security, CRM,
              compliance tracking, all of it — and part of that practice serves real K-12 school
              clients directly, which is why FERPA and COPPA sit on our compliance framework list
              alongside SOC 2 and NIST CSF instead of being an afterthought.
            </p>
            <p className="mt-4 text-[15px] leading-relaxed text-white/75">
              We&apos;re past the &quot;coming soon&quot; stage and past a one-page pitch — the platform
              is built, tested, and running production workloads for our own clients. What we&apos;re
              opening now is a small design-partner cohort: other MSPs running Nexus alongside us,
              shaping what gets built next instead of just waiting for a GA announcement.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="border-y border-white/10 bg-white/[0.015] py-16 md:py-24">
        <div className="container-content">
          <Reveal>
            <div className="mx-auto mb-14 max-w-2xl text-center">
              <span className="text-xs font-bold uppercase tracking-[0.14em] text-nexus-purple-light">
                What we believe
              </span>
              <h2 className="mt-3 font-display text-3xl font-bold text-nexus-ink md:text-[36px]">
                The rules we hold ourselves to, not just the product.
              </h2>
            </div>
          </Reveal>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            {beliefs.map((b, i) => {
              const Icon = b.icon;
              return (
                <Reveal key={b.title} delay={i * 90}>
                  <div className="h-full rounded-2xl border border-white/10 bg-white/[0.02] p-6">
                    <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl bg-nexus-purple/[0.14] text-nexus-purple-light">
                      <Icon fontSize="small" />
                    </div>
                    <h3 className="font-display text-[17px] font-semibold text-nexus-ink">{b.title}</h3>
                    <p className="mt-1.5 text-sm text-white/65">{b.body}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="container-content max-w-[760px] text-center">
          <Reveal>
            <Chip
              label="Building in the open"
              size="small"
              className="mb-4 border border-nexus-purple/30 bg-nexus-purple/[0.14] font-semibold text-nexus-purple-light"
            />
            <h2 className="font-display text-2xl font-bold text-nexus-ink md:text-3xl">
              Read the actual build sequence, not a mission statement.
            </h2>
            <p className="mt-4 text-[15px] leading-relaxed text-white/75">
              The{' '}
              <Link to="/roadmap" className="text-nexus-purple-light no-underline hover:underline">
                roadmap
              </Link>{' '}
              tracks where Nexus actually is, phase by phase, updated by hand as things really move.
              The{' '}
              <Link to="/blog/building-nexus-in-the-open" className="text-nexus-purple-light no-underline hover:underline">
                building-in-the-open post
              </Link>{' '}
              covers why we sequence it that way.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="border-t border-white/10 py-16 md:py-24">
        <div className="container-content">
          <Reveal>
            <div className="rounded-[28px] border border-nexus-purple/35 bg-gradient-to-br from-[#2a1450] to-[#120a26] p-8 text-center md:p-16">
              <h2 className="font-display text-3xl font-bold text-nexus-ink md:text-[36px]">
                Questions? Just email us.
              </h2>
              <p className="mx-auto mt-3 max-w-[520px] text-white/70">
                No sales queue —{' '}
                <a href="mailto:hello@nexusrmm.ai" className="text-nexus-purple-light no-underline hover:underline">
                  hello@nexusrmm.ai
                </a>{' '}
                reaches the team building this directly.
              </p>
              <div className="mt-8 flex justify-center">
                <NotifyForm />
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
